import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Box,
  Card,
  Checkbox,
  DialogContent,
  FormControlLabel,
  Stack,
  Toolbar,
  useTheme
} from "@mui/material";
import styles from "./styles.module.scss";
import { strings } from "../../localization/LocalizedStrings";
import { AlertDialog } from "../AlertDialog";
import { StringFormat } from "../../models/StringFormat";
import { useGetDiscogsIdentity } from "../../hooks/discogs/useGetDiscogsIdentity";
import { StatusCodesHelper } from "../../models/StatusCodesHelper";
import { StatusCodes } from "http-status-codes";
import { useLocation } from "react-router";
import queryString from "query-string";
import { useGrantDiscogsAccess } from "../../hooks/discogs/useGrantDiscogsAccess";
import { IDiscogsCollectionFolder, IDiscogsCollectionFolders } from "../../hooks/discogs";
import { useGetDiscogsCollectionFolders } from "../../hooks/discogs/useGetDiscogsCollectionFolders";
import { RightDetailListItem } from "../RightDetailListItem";
import { useImportDiscogsCollection } from "../../hooks/discogs/useImportDiscogsCollection";
import { useGetDiscogsImportStatus } from "../../hooks/discogs/useGetDiscogsImportStatus";
import { IOngoingTask } from "../../models/OngoingTask";
import { useCancelDiscogsImport } from "../../hooks/discogs/useCancelDiscogsImport";
import { useConfirmDiscogsImport } from "../../hooks/discogs/useConfirmDiscogsImport";
import { CollectionsDialog } from "../collections/CollectionsDialog";
import { ICollection } from "../../hooks/collections";
import { RoutesBuilder } from "../../models/RoutesBuilder";
import { ToolbarButton } from "../toolbar/ToolbarButton";
import { useUser } from "../../hooks/session";
import { UserRole } from "../../hooks/users";
import { Firebase } from "../../services/Firebase";
import { ProductPurchasesDialog } from "../products/ProductPurchasesDialog";
import { ProductType } from "../../hooks/products/interfaces";
import { useCheckDiscogsAccess } from "../../hooks/discogs/useCheckDiscogsAccess";
import { PiperDialog } from "../PiperDialog";
import { IconType } from "../../icons";
import { ImportProgressView } from "../ImportProgressView";
import { PiperDivider } from "../PiperDivider";

export const DiscogsImportDialog: FunctionComponent<IDiscogsDialogProps> = ({
  isOpen,
  setIsOpen,
  isLayout
}) => {
  const [state, setState] = useState<State>("starting");
  let token: string | undefined = undefined;
  let verifier: string | undefined = undefined;
  const [currentUser] = useUser.useState();
  const [intervalId, setIntervalId] = useState<number>();
  const [selectedFolder, setSelectedFolder] = useState<IDiscogsCollectionFolder>();
  const [selectedCollection, setSelectedCollection] = useState<ICollection>();
  const [username, setUsername] = useState("");
  const [folders, setFolders] = useState<IDiscogsCollectionFolders>();
  const [task, setTask] = useState<IOngoingTask>();
  const [shouldSkipDuplicates, setShouldSkipDuplicates] = useState(true);
  const [shouldUploadImage, setShouldUploadImage] = useState(false);
  const getDiscogsIdentity = useGetDiscogsIdentity();
  const grantDiscogsAccess = useGrantDiscogsAccess();
  const getDiscogsFolders = useGetDiscogsCollectionFolders();
  const importDiscogsCollection = useImportDiscogsCollection();
  const getDiscogsImportStatus = useGetDiscogsImportStatus();
  const cancelDiscogsImport = useCancelDiscogsImport();
  const confirmDiscogsImport = useConfirmDiscogsImport();
  const [alertMessage, setAlertMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isPurchaseAlertOpen, setIsPurchaseAlertOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [isCollectionsDialogOpen, setIsCollectionsDialogOpen] = useState(false);
  const [isConfirmImportAlertOpen, setIsConfirmImportAlertOpen] = useState(false);
  const [isCancelImportAlertOpen, setIsCancelImportAlertOpen] = useState(false);
  const { getDiscogsToken } = useCheckDiscogsAccess(
    setAlertMessage,
    setIsAlertOpen,
    setIsLoading,
    () => {}
  );
  const theme = useTheme();
  const location = useLocation();
  const importSubtitle = () => {
    switch (task?.status) {
      case "created":
        return strings.discogs_import_created;
      case "running":
        return strings.discogs_import_subtitle;
      case "finished":
        return strings.discogs_import_finished;
      case "cancelled":
        return strings.discogs_import_cancelled;
      case "errored":
        return strings.discogs_import_errored;
    }
  };
  const getCollectionFolders = async (username: string) => {
    setState("folders");
    const { body, status } = await getDiscogsFolders(username);
    if (StatusCodesHelper.isSuccessful(status)) {
      setFolders(body);
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
    }
    setIsLoading(false);
  };
  const startUp = async () => {
    if (currentUser?.role == "guest") return;
    setIsLoading(true);
    const { body, status } = await getDiscogsImportStatus();
    Firebase.shared.logFirebaseEvent("import_discogs_init");
    if (StatusCodesHelper.isSuccessful(status)) {
      setTask(body);
      if (body.status != "confirmed") {
        setState("importing");
        setIsLoading(false);
      } else {
        await checkDiscogsAccess();
      }
    } else {
      await checkDiscogsAccess();
    }
  };
  const checkDiscogsAccess = async () => {
    const searchParams = queryString.parse(location.search);
    token = searchParams.oauth_token as string;
    verifier = searchParams.oauth_verifier as string;
    const { body, status } = await getDiscogsIdentity();
    if (StatusCodesHelper.isSuccessful(status)) {
      setUsername(body.username);
      await getCollectionFolders(body.username);
    } else if (status == StatusCodes.NOT_FOUND) {
      if (token && verifier) {
        await grantAccess(token, verifier);
      } else {
        await getDiscogsToken();
      }
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
      setIsLoading(false);
    }
  };
  const grantAccess = async (token: string, verifier: string) => {
    const secret = localStorage.getItem("discogs_temp");
    const authLevel = localStorage.getItem("discogs_level");
    if (secret == null || authLevel == null) return;
    localStorage.removeItem("discogs_temp");
    localStorage.removeItem("discogs_level");
    const { body, status } = await grantDiscogsAccess({
      token: token,
      secret: secret,
      authLevel: +authLevel,
      verifier: verifier
    });
    if (StatusCodesHelper.isSuccessful(status)) {
      await checkDiscogsAccess();
    } else {
      setAlertMessage(body.message);
      setIsAlertOpen(true);
      setIsLoading(false);
    }
  };
  const getImportStatus = async () => {
    const { body, status } = await getDiscogsImportStatus();
    if (StatusCodesHelper.isSuccessful(status)) {
      setTask(body);
    }
  };
  useEffect(() => {
    if (!["running", "created"].includes(task?.status)) {
      clearTimer();
      return;
    }
    if (intervalId == undefined) {
      const id = setInterval(async () => {
        await getImportStatus();
      }, 2000);
      setIntervalId(Number(id));
    }
  }, [task]);
  const clearTimer = () => {
    clearInterval(intervalId);
    setIntervalId(undefined);
  };
  useEffect(() => {
    if (!isOpen) return;
    setState("starting");
    startUp().then();
  }, [isOpen]);
  const handleClose = async () => {
    if (isLoading) return;
    clearTimer();
    if (state == "importing" && task && !["running", "created"].includes(task.status)) {
      setIsLoading(true);
      await confirmDiscogsImport();
      setIsLoading(false);
    }
    setIsOpen(false);
  };
  return (
    <PiperDialog
      className={styles.dialog}
      fullWidth
      open={isOpen}
      onClose={handleClose}
      PaperProps={{ style: { minHeight: "320px" } }}
    >
      <Toolbar>
        <h3>{strings.option_discogs_import}</h3>
        <Box flexGrow={1} />
        <ToolbarButton
          href={`${window.location.protocol}//${
            window.location.host
          }${RoutesBuilder.documentation.home()}${RoutesBuilder.documentation.album.importFromDiscogs()}`}
          target={"_blank"}
          tooltip={strings.help}
          iconType={IconType.Help}
        />
        <ToolbarButton
          loading={isLoading}
          onClick={handleClose}
          tooltip={strings.close}
          iconType={IconType.Close}
        />
      </Toolbar>
      <DialogContent>
        {state == "folders" && folders && (
          <Stack spacing={4}>
            <p className={styles.subtitle}>{strings.discogs_folders_subtitle}</p>
            <p className={styles.message}>{strings.discogs_folders_description}</p>
            <Card className={styles.foldersCard} elevation={5}>
              <Stack>
                {folders.folders.map((folder, index) => (
                  <div key={folder.id}>
                    <RightDetailListItem
                      iconType={IconType.Folder}
                      title={folder.name}
                      detail={`${folder.count} ${
                        folder.count == 1 ? strings.album_singular : strings.albums_plural
                      }`}
                      disabled={isLoading}
                      onClick={() => {
                        setSelectedFolder(folder);
                        setIsCollectionsDialogOpen(true);
                      }}
                    />
                    {index < folders.folders.length - 1 && <PiperDivider />}
                  </div>
                ))}
              </Stack>
            </Card>
            <FormControlLabel
              className={styles.checkbox}
              sx={{ width: "100%" }}
              control={
                <Checkbox
                  checked={shouldSkipDuplicates}
                  onChange={() => setShouldSkipDuplicates(state => !state)}
                />
              }
              label={
                <Stack>
                  <p>{strings.discogs_should_skip_duplicates}</p>
                  <p
                    className={styles.checkboxHelpText}
                    style={{ color: theme.palette.primary.subtitle }}
                  >
                    {strings.discogs_should_skip_duplicates_help}
                  </p>
                </Stack>
              }
            />
            <FormControlLabel
              className={styles.checkbox}
              sx={{ width: "100%" }}
              disabled={currentUser?.role != UserRole.premium}
              control={
                <Checkbox
                  checked={shouldUploadImage}
                  onChange={() => setShouldUploadImage(state => !state)}
                />
              }
              label={
                <Stack>
                  <p>{strings.discogs_should_upload_image}</p>
                  <p
                    className={styles.checkboxHelpText}
                    style={{ color: theme.palette.primary.subtitle }}
                  >
                    {currentUser?.role == UserRole.premium
                      ? strings.discogs_should_upload_image_help
                      : strings.discogs_should_upload_image_non_premium}
                  </p>
                </Stack>
              }
            />
          </Stack>
        )}
        {state == "importing" && task && (
          <ImportProgressView
            task={task}
            subtitle={importSubtitle()}
            runningDescription={strings.discogs_import_description}
            disclaimer={
              ["created", "running"].includes(task.status)
                ? strings.discogs_import_disclaimer
                : strings.discogs_import_disclaimer_finished
            }
            isLoading={isLoading}
            onCancel={async () => {
              setIsLoading(true);
              const { body, status } = await cancelDiscogsImport();
              if (StatusCodesHelper.isSuccessful(status)) {
                setTask(body);
              } else {
                setAlertMessage(body.message);
                setIsAlertOpen(true);
              }
              setIsLoading(false);
            }}
          />
        )}
      </DialogContent>
      <CollectionsDialog
        open={isCollectionsDialogOpen}
        setOpen={setIsCollectionsDialogOpen}
        isLayout={isLayout}
        canModifyCollections={false}
        displayStandardCollections={true}
        onCollectionSelected={collection => {
          setSelectedCollection(collection);
          setIsConfirmImportAlertOpen(true);
        }}
        selectedCollections={[]}
        forceLoading={false}
        canNegate={false}
      />
      <AlertDialog
        message={StringFormat(
          strings.discogs_folders_confirmation,
          String(selectedFolder?.count),
          selectedFolder?.name ?? "",
          selectedCollection?.name ?? ""
        )}
        open={isConfirmImportAlertOpen}
        setOpen={setIsConfirmImportAlertOpen}
        isConfirm
        onConfirm={async () => {
          setIsCollectionsDialogOpen(false);
          setIsLoading(true);
          const { body, status } = await importDiscogsCollection({
            username,
            folderId: selectedFolder?.id ?? 0,
            collections: selectedCollection ? [selectedCollection] : [],
            shouldUploadImage,
            shouldSkipDuplicates
          });
          if (StatusCodesHelper.isSuccessful(status)) {
            setTask(body);
            setState("importing");
          } else if (StatusCodesHelper.isLimitReached(status)) {
            setAlertMessage(body.message);
            setIsPurchaseAlertOpen(true);
          } else {
            setAlertMessage(body.message);
            setIsAlertOpen(true);
          }
          setIsLoading(false);
        }}
      />
      <AlertDialog
        message={strings.discogs_import_cancel_confirmation}
        open={isCancelImportAlertOpen}
        setOpen={setIsCancelImportAlertOpen}
        isConfirm
        onConfirm={async () => {
          setIsLoading(true);
          const { body, status } = await cancelDiscogsImport();
          if (StatusCodesHelper.isSuccessful(status)) {
            setTask(body);
          } else {
            setAlertMessage(body.message);
            setIsAlertOpen(true);
          }
          setIsLoading(false);
        }}
      />
      <AlertDialog message={alertMessage} open={isAlertOpen} setOpen={setIsAlertOpen} />
      <AlertDialog
        message={alertMessage}
        open={isPurchaseAlertOpen}
        setOpen={setIsPurchaseAlertOpen}
        isConfirm
        confirmTitle={strings.purchase_alert}
        onConfirm={() => setIsPurchaseDialogOpen(true)}
      />
      <ProductPurchasesDialog
        productType={ProductType.ALBUMS}
        open={isPurchaseDialogOpen}
        setOpen={setIsPurchaseDialogOpen}
      />
    </PiperDialog>
  );
};

type State = "starting" | "folders" | "importing";

export interface IDiscogsDialogProps {
  isOpen: boolean;
  setIsOpen: (newValue: boolean) => void;
  isLayout: boolean;
}
